// UnitMismatchAdvisor.js
// Inline banner inside QuantityCalculatorModal for unit mismatch detection.
//
// Supports three input modes depending on the material type:
//   'thickness'    — user types mm → factor  (concrete, fill, masonry)
//   'face_percent' — BRANZ face% + stud depth → m³/m²  (timber wall framing)
//   'factor'       — pre-calculated ratio picker  (floor/roof framing, CLT, steel)

import React, { useState, useEffect } from 'react';
import { analyseMismatch, getThicknessPreset, formatFactor } from './unitConversionUtils';
import './UnitMismatchAdvisor.css';

function UnitMismatchAdvisor({ compositeUnit, materialUnit, materialName, onApplyFactor }) {
  const mismatch = analyseMismatch(compositeUnit, materialUnit);
  const preset   = getThicknessPreset(materialName);

  // ── Shared state ─────────────────────────────────────────────────────────────
  const [applied,   setApplied]   = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // ── face_percent state ────────────────────────────────────────────────────────
  const [facePercent,     setFacePercent]     = useState('');
  const [selectedDepth,   setSelectedDepth]   = useState(null);
  const [useFallback,     setUseFallback]     = useState(false); // toggle to centres mode
  const [fallbackOption,  setFallbackOption]  = useState(null);

  // ── factor state ──────────────────────────────────────────────────────────────
  const [selectedOption, setSelectedOption] = useState(null);

  // ── thickness state ───────────────────────────────────────────────────────────
  const [inputMm, setInputMm] = useState('');

  const inputMode = preset?.inputMode ?? 'thickness';

  // Pre-fill defaults when advisor mounts / material changes
  useEffect(() => {
    if (!mismatch.hasMismatch) return;
    setApplied(false);
    setCollapsed(false);
    setUseFallback(false);

    if (preset?.inputMode === 'face_percent') {
      setFacePercent(String(preset.defaultFacePercent ?? 36));
      setSelectedDepth(preset.depthOptions?.[0] ?? null);
      const fb = preset.fallbackOptions?.find(o => o.label === preset.fallbackDefaultLabel)
               ?? preset.fallbackOptions?.[0] ?? null;
      setFallbackOption(fb);

    } else if (preset?.inputMode === 'factor') {
      const def = preset.options?.find(o => o.label === preset.defaultLabel) ?? preset.options?.[0];
      setSelectedOption(def ?? null);

    } else {
      setInputMm(String(preset?.default ?? ''));
    }
  }, [compositeUnit, materialUnit, materialName]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mismatch.hasMismatch) return null;

  // ── Derived factor ────────────────────────────────────────────────────────────
  let factor = null;
  let isValid = false;
  let factorDescription = '';

  if (inputMode === 'face_percent') {
    if (useFallback) {
      isValid = fallbackOption !== null;
      factor  = fallbackOption?.value ?? null;
      factorDescription = fallbackOption?.label ?? '';
    } else {
      const pct = parseFloat(facePercent);
      isValid = !isNaN(pct) && pct > 0 && pct <= 100 && selectedDepth !== null;
      if (isValid) {
        factor = parseFloat(((pct / 100) * (selectedDepth.depth / 1000)).toFixed(6));
        factorDescription = `${pct}% face × ${selectedDepth.depth} mm depth (BRANZ)`;
      }
    }
  } else if (inputMode === 'factor') {
    isValid = selectedOption !== null;
    factor  = selectedOption?.value ?? null;
    factorDescription = selectedOption?.label ?? '';
  } else {
    const mm = parseFloat(inputMm);
    isValid  = !isNaN(mm) && mm > 0;
    factor   = isValid ? mismatch.convert(mm) : null;
    factorDescription = isValid ? `${mm} mm` : '';
  }

  const unitRatioLabel = (mismatch.type && mismatch.type !== 'other')
    ? `${materialUnit ?? ''} per ${compositeUnit ?? ''}`
    : '';

  // ── Apply ─────────────────────────────────────────────────────────────────────
  const handleApply = () => {
    if (!isValid) return;
    const desc = `${materialName ? materialName + ' — ' : ''}${factorDescription}`;
    onApplyFactor(factor, mismatch.applyToField, desc);
    setApplied(true);
    setCollapsed(true);
  };

  const reset = () => { setCollapsed(false); setApplied(false); };

  // ── Collapsed ─────────────────────────────────────────────────────────────────
  if (collapsed) {
    return (
      <div className="uma uma--success">
        <span className="uma__icon">✓</span>
        <span className="uma__collapsed-text">
          Conversion applied — factor&nbsp;<strong>{formatFactor(factor)}</strong>
          {unitRatioLabel && <span className="uma__factor-desc"> ({unitRatioLabel})</span>}
          &nbsp;added to calculator.
        </span>
        <button className="uma__btn-ghost" onClick={reset}>Edit</button>
      </div>
    );
  }

  // ── Shared header ─────────────────────────────────────────────────────────────
  const Header = () => (
    <>
      <div className="uma__header">
        <span className="uma__icon">⚠️</span>
        <div className="uma__header-text">
          <strong>Unit mismatch detected</strong>
          <span className="uma__unit-tags">
            <span className="uma__tag uma__tag--composite">Composite: <b>{compositeUnit}</b></span>
            <span className="uma__arrow">→</span>
            <span className="uma__tag uma__tag--material">Material: <b>{materialUnit}</b></span>
          </span>
        </div>
      </div>
      <p className="uma__explanation">{mismatch.label}</p>
      {preset?.hint && !useFallback && (
        <p className="uma__hint">💡 <em>{preset.hint}</em></p>
      )}
    </>
  );

  // ── Shared apply row ──────────────────────────────────────────────────────────
  const ApplyRow = () => (
    <div className="uma__apply-row">
      {isValid && (
        <div className="uma__factor-preview">
          {inputMode === 'face_percent' && !useFallback && (
            <span className="uma__factor-calc">
              {parseFloat(facePercent)}% ÷ 100 × {selectedDepth?.depth} mm ÷ 1000 =&nbsp;
            </span>
          )}
          <span className="uma__factor-label">Factor:</span>
          <span className="uma__factor-value">{formatFactor(factor)}</span>
          {unitRatioLabel && <span className="uma__factor-desc">{unitRatioLabel}</span>}
        </div>
      )}
      <button
        className="uma__btn-apply"
        onClick={handleApply}
        disabled={!isValid}
      >
        Apply ↓
      </button>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // MODE: face_percent  (BRANZ framing face percentage — wall framing)
  // ════════════════════════════════════════════════════════════════════════════
  if (inputMode === 'face_percent') {
    return (
      <div className="uma">
        <Header />

        {/* Method toggle */}
        <div className="uma__method-toggle">
          <button
            type="button"
            className={`uma__toggle-btn ${!useFallback ? 'uma__toggle-btn--active' : ''}`}
            onClick={() => { setUseFallback(false); setApplied(false); }}
          >
            BRANZ face %
          </button>
          <button
            type="button"
            className={`uma__toggle-btn ${useFallback ? 'uma__toggle-btn--active' : ''}`}
            onClick={() => { setUseFallback(true); setApplied(false); }}
          >
            Framing centres
          </button>
        </div>

        {/* ── BRANZ face % method ── */}
        {!useFallback && (
          <div className="uma__face-percent">
            <div className="uma__face-row">

              {/* Face % input */}
              <div className="uma__face-field">
                <label className="uma__input-label">Face %</label>
                <div className="uma__input-wrapper">
                  <input
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    className={`uma__input uma__input--short ${
                      facePercent !== '' && (parseFloat(facePercent) <= 0 || parseFloat(facePercent) > 100)
                        ? 'uma__input--error' : ''
                    }`}
                    value={facePercent}
                    onChange={(e) => { setFacePercent(e.target.value); setApplied(false); }}
                  />
                  <span className="uma__input-unit">%</span>
                </div>
                {/* Common face% chips */}
                <div className="uma__preset-chips" style={{ marginTop: 6 }}>
                  {[20, 25, 30, 36, 40, 45, 50].map(pct => (
                    <button
                      key={pct}
                      type="button"
                      className={`uma__chip ${String(pct) === facePercent ? 'uma__chip--active' : ''}`}
                      onClick={() => { setFacePercent(String(pct)); setApplied(false); }}
                    >
                      {pct}%
                    </button>
                  ))}
                </div>
                <p className="uma__field-note">BRANZ default: 36%</p>
              </div>

              <span className="uma__face-multiply">×</span>

              {/* Stud depth */}
              <div className="uma__face-field">
                <label className="uma__input-label">Stud depth</label>
                <div className="uma__preset-chips">
                  {(preset.depthOptions ?? []).map(opt => (
                    <button
                      key={opt.depth}
                      type="button"
                      className={`uma__chip ${selectedDepth?.depth === opt.depth ? 'uma__chip--active' : ''}`}
                      onClick={() => { setSelectedDepth(opt); setApplied(false); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="uma__field-note">Stud depth (not width)</p>
              </div>

            </div>
          </div>
        )}

        {/* ── Fallback: framing centres picker ── */}
        {useFallback && (
          <div className="uma__factor-mode">
            <p className="uma__factor-mode-label">Select framing configuration:</p>
            <div className="uma__option-list">
              {(preset.fallbackOptions ?? []).map(opt => (
                <button
                  key={opt.label}
                  type="button"
                  className={`uma__option-btn ${fallbackOption?.label === opt.label ? 'uma__option-btn--active' : ''}`}
                  onClick={() => { setFallbackOption(opt); setApplied(false); }}
                >
                  <span className="uma__option-label">{opt.label}</span>
                  <span className="uma__option-value">{formatFactor(opt.value)}&nbsp;{unitRatioLabel}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <ApplyRow />

        {applied && (
          <p className="uma__applied-msg">
            ✓ Row added with factor <strong>{formatFactor(factor)}</strong>.
            Adjust Count or other dimensions as needed.
          </p>
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // MODE: factor  (pre-calculated ratio picker — floor/roof framing, CLT, etc.)
  // ════════════════════════════════════════════════════════════════════════════
  if (inputMode === 'factor') {
    return (
      <div className="uma">
        <Header />
        <div className="uma__factor-mode">
          <p className="uma__factor-mode-label">Select configuration:</p>
          <div className="uma__option-list">
            {(preset?.options ?? []).map(opt => (
              <button
                key={opt.label}
                type="button"
                className={`uma__option-btn ${selectedOption?.label === opt.label ? 'uma__option-btn--active' : ''}`}
                onClick={() => { setSelectedOption(opt); setApplied(false); }}
              >
                <span className="uma__option-label">{opt.label}</span>
                <span className="uma__option-value">{formatFactor(opt.value)}&nbsp;{unitRatioLabel}</span>
              </button>
            ))}
          </div>
        </div>
        <ApplyRow />
        {applied && (
          <p className="uma__applied-msg">
            ✓ Row added with factor <strong>{formatFactor(factor)}</strong>. Adjust Count or dimensions as needed.
          </p>
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // MODE: thickness  (concrete, fill, masonry, insulation, render)
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div className="uma">
      <Header />
      <div className="uma__input-row">
        <label className="uma__input-label" htmlFor="uma-thickness-input">
          {mismatch.inputLabel}
        </label>
        <div className="uma__input-wrapper">
          <input
            id="uma-thickness-input"
            type="number"
            min="0"
            step="any"
            className={`uma__input ${!isValid && inputMm !== '' ? 'uma__input--error' : ''}`}
            value={inputMm}
            onChange={(e) => { setInputMm(e.target.value); setApplied(false); }}
            placeholder={`e.g. ${preset?.default ?? 100}`}
          />
          <span className="uma__input-unit">{mismatch.inputUnit}</span>
        </div>
        {preset?.options?.length > 0 && (
          <div className="uma__preset-chips">
            {preset.options.map(mm => (
              <button
                key={mm}
                type="button"
                className={`uma__chip ${String(mm) === inputMm ? 'uma__chip--active' : ''}`}
                onClick={() => { setInputMm(String(mm)); setApplied(false); }}
              >
                {mm} mm
              </button>
            ))}
          </div>
        )}
      </div>
      <ApplyRow />
      {applied && (
        <p className="uma__applied-msg">
          ✓ Row added with factor <strong>{formatFactor(factor)}</strong>. Adjust Count or dimensions as needed.
        </p>
      )}
    </div>
  );
}

export default UnitMismatchAdvisor;